import { useEffect, useRef, useState } from "react";

const INITIAL_WIDTH = 430;
const MIN_WIDTH = 430;
const MOBILE_BREAKPOINT = 1080;
const clamp = (width, min, max) => Math.min(max, Math.max(min, width));

export function useAssistantResize(panel) {
  const [width, setWidth] = useState(INITIAL_WIDTH);
  const [bounds, setBounds] = useState({ min: MIN_WIDTH, max: INITIAL_WIDTH });
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);

  useEffect(() => {
    const container = panel.current?.parentElement;
    if (!container) return;
    const measure = () => {
      const max = Math.floor(container.getBoundingClientRect().width);
      if (!max) return;
      const min = Math.min(MIN_WIDTH, max);
      setBounds((previous) =>
        previous.min === min && previous.max === max ? previous : { min, max },
      );
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        setWidth((previous) => clamp(previous, min, max));
      } else {
        drag.current = null;
        setDragging(false);
      }
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(container);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [panel]);

  const changeWidth = (next) => setWidth(clamp(next, bounds.min, bounds.max));
  const stopDragging = (event) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return {
    width,
    bounds,
    dragging,
    handleProps: {
      onPointerDown: (event) => {
        if (event.button !== 0 || drag.current || window.innerWidth <= MOBILE_BREAKPOINT) return;
        event.preventDefault();
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
          pointerId: event.pointerId,
          x: event.clientX,
          width: panel.current.getBoundingClientRect().width,
        };
        setDragging(true);
      },
      onPointerMove: (event) => {
        if (drag.current?.pointerId !== event.pointerId) return;
        changeWidth(drag.current.width + drag.current.x - event.clientX);
      },
      onPointerUp: stopDragging,
      onPointerCancel: stopDragging,
      onLostPointerCapture: stopDragging,
      onDoubleClick: () => changeWidth(INITIAL_WIDTH),
      onKeyDown: (event) => {
        const next = {
          ArrowLeft: width + 40,
          ArrowRight: width - 40,
          Home: bounds.min,
          End: bounds.max,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        changeWidth(next);
      },
    },
  };
}
